import React, { useContext, useEffect, useMemo, useState } from 'react';
import * as R from 'ramda';

import InventoryAssetTree from '@atom/components/common/inventoryAssetTree/InventoryAssetTree';
import { TYPE_LABELS } from '@atom/components/common/requests/assetRequestConstants';
import { getCustomValues } from '@atom/components/common/requests/customTenantUtilities';
import { GET_ASSET } from '@atom/graph/asset';
import client from '@atom/graph/client';
import { Icon, IconButton, Modal, Select } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  AssetRequestCreateInput,
  AssetRequestType,
} from '@atom/types/assetRequest';
import {
  AssetBudget,
  AssetConnectionItem,
  AssetDetail,
} from '@atom/types/inventory';
import api from '@atom/utilities/api';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';
import {
  Client,
  isCurrentClient,
} from '@atom/utilities/featureToggleUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import RequestsContext from '../RequestsContext';

import ARDOT118TransferCreate from './ardotCreate/ARDOT118TransferCreate';
import SDDOTAdjustmentCreate from './sddotCreate/SDDOTAdjustmentCreate';
import SDDOTInventoryCreate from './sddotCreate/SDDOTInventoryCreate';
import SDDOTReplenishmentCreate from './sddotCreate/SDDOTReplenishmentCreate';
import SDDOTStockpileCreate from './sddotCreate/SDDOTStockpileCreate';

const { MenuItem } = Select;

const styles = {
  modal: {
    padding: '1.5rem',
  },
  select: {
    maxWidth: '28.25rem',
  },
  back: {
    marginRight: '1rem',
  },
  category: {
    minHeight: '32rem',
  },
};

export enum View {
  MAIN = 'MAIN',
  CATEGORY = 'CATEGORY',
}

interface Props {
  open: boolean;
  onClose: () => void;
  createInputs: AssetRequestCreateInput;
  updateCreateInput: (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => void;
  onConfirm: () => void;
  updateType: (type: any) => void;
}

const CreateRequestModal = ({
  open,
  onClose,
  createInputs,
  updateCreateInput,
  onConfirm,
  updateType,
}: Props) => {
  const { schema, asset } = useContext(RequestsContext);

  const [view, setView] = useState<View>(View.MAIN);
  const [budget, setBudget] = useState<AssetBudget>();
  const [budgetLoading, setBudgetLoading] = useState<boolean>(false);
  const [selectedFromAsset, setSelectedFromAsset] =
    useState<AssetConnectionItem>();
  const [transferToggle, setTransferToggle] = useState<boolean>(false);

  // TODO: [AM-16707]: Temp code until BE solution is built
  const [totalRemaining, setTotalRemaining] = useState<number>(0);
  const [totalRemainingLoading, setTotalRemainingLoading] =
    useState<boolean>(false);
  const [fromTotalRemaining, setFromTotalRemaining] = useState<number>(0);
  const [fromTotalRemainingLoading, setFromTotalRemainingLoading] =
    useState<boolean>(false);

  const getTotalRemaining = async () => {
    setTotalRemainingLoading(true);
    const { data } = await api.get(
      `${INVENTORY_ASSETS_ENDPOINT}/${asset?.id}/usageSummary`,
    );

    setTotalRemaining(R.pathOr(0, ['totalRemaining'], data));
    setTotalRemainingLoading(false);
  };

  useEffect(() => {
    if (asset?.id) {
      getTotalRemaining();
    }
  }, [asset?.id]);

  const getFromTotalRemaining = async () => {
    setFromTotalRemainingLoading(true);
    const { data } = await api.get(
      `${INVENTORY_ASSETS_ENDPOINT}/${selectedFromAsset?.id}/usageSummary`,
    );

    setFromTotalRemaining(R.pathOr(0, ['totalRemaining'], data));
    setFromTotalRemainingLoading(false);
  };

  useEffect(() => {
    if (selectedFromAsset?.id) {
      getFromTotalRemaining();
    }
  }, [selectedFromAsset?.id]);

  const getBudget = async () => {
    setBudgetLoading(true);
    const { data } = await client.query<{ asset: AssetDetail }, { id: string }>(
      {
        query: GET_ASSET,
        fetchPolicy: 'network-only',
        variables: {
          id: asset.id,
        },
      },
    );

    setBudget(data?.asset?.budget);
    setBudgetLoading(false);
  };

  useEffect(() => {
    if (open && isCurrentClient([Client.SDDOT])) {
      getBudget();
      setSelectedFromAsset(null);
      setView(View.MAIN);
    }
  }, [open]);

  useEffect(() => {
    setSelectedFromAsset(null);
  }, [createInputs.type]);

  const createProps = {
    open,
    createInputs,
    updateCreateInput,
    budget,
    asset,
    // TODO: [AM-16707]: Temp code until BE solution is built
    totalRemaining,
    fromTotalRemaining,
    totalRemainingLoading,
    fromTotalRemainingLoading,
  };

  const modalContent = {
    [AssetRequestType.SDDOT_INVENTORY_TRANSFER]: (
      <SDDOTInventoryCreate
        {...createProps}
        selectedFromAsset={selectedFromAsset}
        setSelectedFromAsset={setSelectedFromAsset}
      />
    ),
    [AssetRequestType.SDDOT_STOCKPILE_TRANSFER]: (
      <SDDOTStockpileCreate
        {...createProps}
        selectedFromAsset={selectedFromAsset}
        setSelectedFromAsset={setSelectedFromAsset}
      />
    ),
    [AssetRequestType.SDDOT_REPLENISHMENT]: (
      <SDDOTReplenishmentCreate {...createProps} />
    ),
    [AssetRequestType.SDDOT_ADJUSTMENT]: (
      <SDDOTAdjustmentCreate {...createProps} />
    ),
    [AssetRequestType.ARDOT_118_TRANSFER]: (
      <ARDOT118TransferCreate
        {...createProps}
        setView={setView}
        transferToggle={transferToggle}
        setTransferToggle={setTransferToggle}
      />
    ),
  };

  const disabled = useMemo(() => {
    const {
      replenishment,
      quantityOrdered,
      quantity,
      reason,
      fromAssetId,
      assignedTo,
      reviewer,
      receiver,
      toCategoryId,
      attributes,
    } = createInputs;

    // TODO: [AM-16707]: Temp code until BE solution is built
    const inventoryTransferDifference =
      Number(fromTotalRemaining) - Number(quantityOrdered);
    const stockpileTransferDifference =
      Number(fromTotalRemaining) - Number(quantity);
    const adjustmentUpdatedQuantity = Number(totalRemaining) + Number(quantity);

    // const inventoryTransferDifference =
    //   Number(selectedFromAsset?.budget?.quantityOnHand) -
    //   Number(quantityOrdered);
    // const stockpileTransferDifference =
    //   Number(selectedFromAsset?.budget?.quantityOnHand) - Number(quantity);
    // const adjustmentUpdatedQuantity =
    //   Number(asset?.budget?.quantityOnHand) + Number(quantity);

    const attributeChangeCount = !isNilOrEmpty(attributes)
      ? attributes.reduce((count, attribute) => {
          return attribute.newValue !== attribute.oldValue ? count + 1 : count;
        }, 0)
      : 0;

    const disabledRules = {
      [AssetRequestType.SDDOT_INVENTORY_TRANSFER]:
        totalRemainingLoading ||
        fromTotalRemainingLoading ||
        isNilOrEmpty(fromAssetId) ||
        isNilOrEmpty(quantityOrdered) ||
        inventoryTransferDifference < 0 ||
        isNilOrEmpty(assignedTo),
      [AssetRequestType.SDDOT_STOCKPILE_TRANSFER]:
        totalRemainingLoading ||
        fromTotalRemainingLoading ||
        isNilOrEmpty(fromAssetId) ||
        isNilOrEmpty(quantity) ||
        stockpileTransferDifference < 0,
      [AssetRequestType.SDDOT_ADJUSTMENT]:
        totalRemainingLoading ||
        isNilOrEmpty(quantity) ||
        adjustmentUpdatedQuantity < 0 ||
        isNilOrEmpty(reason),
      [AssetRequestType.SDDOT_REPLENISHMENT]:
        isNilOrEmpty(quantityOrdered) ||
        isNilOrEmpty(replenishment?.invoiceNumber) ||
        isNilOrEmpty(replenishment?.quantityReceived) ||
        isNilOrEmpty(replenishment?.unitCost) ||
        isNilOrEmpty(replenishment?.totalCost),
      [AssetRequestType.ARDOT_118_TRANSFER]:
        isNilOrEmpty(reviewer) ||
        isNilOrEmpty(receiver) ||
        (transferToggle && isNilOrEmpty(toCategoryId)) ||
        (attributeChangeCount === 0 && isNilOrEmpty(toCategoryId)),
    };

    return disabledRules[createInputs?.type];
  }, [
    createInputs,
    // TODO: [AM-16707]: Temp code until BE solution is built
    totalRemaining,
    fromTotalRemaining,
    totalRemainingLoading,
    fromTotalRemainingLoading,
  ]);

  const { typeOptions } = getCustomValues(schema, asset);
  const hideTypeFilter = isCurrentClient([Client.ARDOT]);

  const modalWidth =
    createInputs?.type === AssetRequestType.SDDOT_REPLENISHMENT ? 'xl' : 'sm';

  const getTitle = (): React.ReactNode => {
    if (view === View.CATEGORY) {
      return (
        <>
          <IconButton style={styles.back} onClick={() => setView(View.MAIN)}>
            <Icon>arrow_back</Icon>
          </IconButton>
          To District or Division
        </>
      );
    }

    return 'Create Inventory Request';
  };

  const handleCategorySelect = (category: any, path: any) => {
    if (isNilOrEmpty(category?.categories)) {
      updateCreateInput(null, 'reviewer');
      updateCreateInput(null, 'receiver');
      updateCreateInput(category?.id, 'toCategoryId');
      updateCreateInput(
        path.map(item => item.name).join(' / '),
        'toCategoryPath',
      );

      setView(View.MAIN);
    }
  };

  const getContent = (): React.ReactNode => {
    const content = {
      [View.MAIN]: (
        <div>
          {!hideTypeFilter && (
            <Select
              key="type"
              label="Request Type"
              style={styles.select}
              InputLabelProps={{ style: { color: colors.neutral.dim } }}
              fullWidth
              onChange={event => updateType(event.target.value)}
              value={createInputs.type}
              data-cy="assetRequestTypeSelect"
            >
              {typeOptions.map((option: string, index: number) => (
                <MenuItem key={index} value={option}>
                  {TYPE_LABELS[option]}
                </MenuItem>
              ))}
            </Select>
          )}
          {modalContent[createInputs.type]}
        </div>
      ),
      [View.CATEGORY]: (
        <div style={styles.category}>
          <InventoryAssetTree
            includeAssets={false}
            search="categories"
            onCategoryClick={(category, path) =>
              handleCategorySelect(category, path)
            }
            disabledCategoryIds={
              new Set(['inventory', createInputs.categoryId])
            }
            schemaId={schema?.id}
            includeMaterials
            onlyPublishedCategories
          />
        </div>
      ),
    };

    return content[view];
  };

  return (
    <Modal
      title={getTitle()}
      open={open}
      onCancel={onClose}
      onConfirm={onConfirm}
      confirmButtonText="Create"
      data-cy="createAssetRequest"
      contentStyle={styles.modal}
      disabled={disabled}
      width={modalWidth}
      loading={budgetLoading}
      disableCloseOnOutsideClick
      disableFooter={view === View.CATEGORY}
    >
      {getContent()}
    </Modal>
  );
};

export default CreateRequestModal;
